import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import ContactForm from './ContactForm';
import './KimoneSellsOrlando.css';

const KimoneSellsOrlando = () => {
  const [showForm, setShowForm] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    // Get QR code from URL params or generate one for direct visitors
    const urlParams = new URLSearchParams(window.location.search);
    const qr = urlParams.get('qr') || `KIMONE_DIRECT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setQrCode(qr);

    // Track visit
    setTimeout(() => {
      fetch('https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/track-visit', {
        method: 'POST',
        body: JSON.stringify({
          qrCode: qr,
          ipAddress: window.location.hostname
        })
      })
      .then(response => response.json())
      .then(data => console.log('Visit tracked:', data))
      .catch(error => console.error('Error tracking visit:', error));
    }, 1000);

    // Scroll listener for scroll-to-top button
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContactClick = () => {
    setShowForm(true);
  };

  const [showSocialPopup, setShowSocialPopup] = useState(false);

  return (
    <div className="kimone-sells-orlando">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Kimone Campbell - Orlando Real Estate Agent | LPT Realty</title>
        <meta name="description" content="Your Trusted Local Guide to navigate Central Florida's Real Estate with confidence. Orlando realtor specializing in buying, selling, and investment properties. Call (321) 522-2149" />
        <meta name="keywords" content="Orlando realtor, Central Florida real estate, Orlando homes for sale, Kissimmee realtor, Winter Park homes, Orlando real estate agent, buy home Orlando, sell home Orlando, Kimone Campbell" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Kimone Campbell - Your Trusted Orlando Real Estate Guide" />
        <meta property="og:description" content="Navigate Central Florida's Real Estate with confidence. Expert realtor serving Orlando, Kissimmee, Winter Park and surrounding areas." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://kimonesellsorlando.com" />
        <meta property="og:image" content="https://kimonesellsorlando.com/media/kimone/profile.jpg" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kimone Campbell - Orlando Real Estate Agent" />
        <meta name="twitter:description" content="Your Trusted Local Guide to navigate Central Florida's Real Estate with confidence." />
        <meta name="twitter:image" content="https://kimonesellsorlando.com/media/kimone/profile.jpg" />

        {/* Structured Data - LocalBusiness Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Kimone Campbell",
            "image": "https://kimonesellsorlando.com/media/kimone/profile.jpg",
            "telephone": "(321) 522-2149",
            "url": "https://kimonesellsorlando.com",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Orlando",
              "addressRegion": "FL",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "28.5383",
              "longitude": "-81.3792"
            },
            "areaServed": [
              "Orlando, FL",
              "Kissimmee, FL",
              "Winter Park, FL",
              "Central Florida"
            ],
            "priceRange": "$$",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "reviewCount": "34"
            },
            "sameAs": [
              "https://www.youtube.com/@homesbykimone",
              "https://www.facebook.com/kimonesellsorlando",
              "https://www.instagram.com/kimonesellsorlando",
              "https://www.linkedin.com/in/kimone-campbell"
            ]
          })}
        </script>

        {/* Structured Data - Person Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Kimone Campbell",
            "jobTitle": "Real Estate Agent",
            "worksFor": {
              "@type": "Organization",
              "name": "LPT Realty LLC"
            },
            "telephone": "(321) 522-2149",
            "url": "https://kimonesellsorlando.com",
            "image": "https://kimonesellsorlando.com/media/kimone/profile.jpg",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Orlando",
              "addressRegion": "FL",
              "addressCountry": "US"
            },
            "knowsAbout": [
              "Real Estate",
              "Home Buying",
              "Home Selling",
              "Investment Properties",
              "Central Florida Real Estate Market"
            ]
          })}
        </script>
      </Helmet>

      {/* Header with Navigation */}
      <header className="kso-header">
        <div className="kso-header-content">
          <div className="kso-logo">
            <img src="/media/kimone/logo.png" alt="LPT Realty" className="kso-lpt-logo" />
            <div className="kso-logo-text">
              <h1>KIMONE CAMPBELL</h1>
              <p className="kso-subtitle">Your Trusted Orlando Real Estate Guide</p>
            </div>
          </div>
          <nav className="kso-nav">
            <a href="#home">Home</a>
            <a href="#services">Services</a>
            <a href="#properties">Properties</a>
            <a href="#reviews">Reviews</a>
            <a href="#about">About</a>
            <a href="#contact" className="kso-nav-cta">Contact</a>
          </nav>
          <div className="kso-header-icons">
            <a href="mailto:kimone@lptrealty.com" className="kso-icon-link" title="Email">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </a>
            <a href="tel:321-522-2149" className="kso-icon-link" title="Call (321) 522-2149">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </a>
            <button className="kso-icon-link kso-user-button" onClick={() => setShowSocialPopup(!showSocialPopup)} title="Sign up or Sign in" aria-label="Sign up or Sign in">
              <svg className="kso-user-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
              </svg>
            </button>
            <button className="kso-icon-link kso-share-button" onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'Kimone Campbell - Orlando Real Estate',
                  text: 'Your Trusted Local Guide to Central Florida Real Estate',
                  url: window.location.href
                });
              } else {
                alert('Share this page: ' + window.location.href);
              }
            }} title="Share">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" fill="none"/>
                <circle cx="6" cy="12" r="3" fill="none"/>
                <circle cx="18" cy="19" r="3" fill="none"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="kso-hero">
        <video className="kso-hero-video" autoPlay muted loop playsInline>
          <source src="/media/kimone/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="kso-hero-overlay"></div>
        <div className="kso-hero-content">
          <h2 className="kso-hero-title">Find Your Dream Home in Central Florida</h2>
          <p className="kso-hero-subtitle">Navigate Central Florida's Real Estate with Confidence</p>
          <div className="kso-hero-search">
            <input
              type="text"
              placeholder="Enter an address, neighborhood, city, or ZIP code"
              className="kso-search-input"
            />
            <button className="kso-search-button">Search Properties</button>
          </div>
        </div>
      </section>

      {/* Real Estate Journey Tiles */}
      <section id="services" className="kso-services">
        <div className="kso-container">
          <h2 className="kso-section-title">Your Real Estate Journey Starts Here</h2>
          <div className="kso-services-grid">
            <div className="kso-service-tile">
              <div className="kso-tile-image" style={{backgroundImage: 'url(/media/kimone/buying-home.jpg)'}}>
                <div className="kso-tile-overlay"></div>
              </div>
              <div className="kso-tile-content">
                <h3>Buying a Home</h3>
                <p>Let me guide you through every step of finding and purchasing your perfect Central Florida home.</p>
                <button className="kso-tile-button" onClick={handleContactClick}>Start Your Search</button>
              </div>
            </div>

            <div className="kso-service-tile">
              <div className="kso-tile-image" style={{backgroundImage: 'url(/media/kimone/selling-home.jpg)'}}>
                <div className="kso-tile-overlay"></div>
              </div>
              <div className="kso-tile-content">
                <h3>Selling a Home</h3>
                <p>Get top dollar for your property with expert marketing and personalized service.</p>
                <button className="kso-tile-button" onClick={handleContactClick}>Get Your Home Value</button>
              </div>
            </div>

            <div className="kso-service-tile">
              <div className="kso-tile-image" style={{backgroundImage: 'url(/media/kimone/home-value.jpg)'}}>
                <div className="kso-tile-overlay"></div>
              </div>
              <div className="kso-tile-content">
                <h3>Your Home's Value</h3>
                <p>Discover what your home is worth in today's Central Florida market.</p>
                <button className="kso-tile-button" onClick={handleContactClick}>Free Home Valuation</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section id="reviews" className="kso-reviews">
        <div className="kso-container">
          <h2 className="kso-section-title">What My Clients Say</h2>
          <p className="kso-reviews-subtitle">34 Five-Star Reviews on Google</p>

          {/* Google Reviews Widget */}
          <div className="kso-google-reviews">
            <div
              className="elfsight-app-fb6c7f8e-86d9-4c3e-9e5f-3c3e8d7a1234"
              data-elfsight-app-lazy
            ></div>

            {/* Reviews Display */}
            <div className="kso-reviews-grid">
              <div className="kso-review-card">
                <div className="kso-review-stars">⭐⭐⭐⭐⭐</div>
                <p className="kso-review-text">"Kimone was exceptional! She made our home buying process smooth and stress-free. Highly recommend!"</p>
                <p className="kso-review-author">- Google Review</p>
              </div>
              <div className="kso-review-card">
                <div className="kso-review-stars">⭐⭐⭐⭐⭐</div>
                <p className="kso-review-text">"Best realtor in Orlando! Kimone's knowledge of the Central Florida market is unmatched."</p>
                <p className="kso-review-author">- Google Review</p>
              </div>
              <div className="kso-review-card">
                <div className="kso-review-stars">⭐⭐⭐⭐⭐</div>
                <p className="kso-review-text">"Professional, responsive, and truly cares about her clients. We couldn't be happier with our new home!"</p>
                <p className="kso-review-author">- Google Review</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section id="properties" className="kso-properties">
        <div className="kso-container">
          <h2 className="kso-section-title">Featured Properties</h2>
          <div className="kso-properties-grid">
            {/* Placeholder properties - Kimone will upload actual content */}
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} className="kso-property-card">
                <div className="kso-property-image">
                  <img src={`/media/kimone/property-${item}.jpg`} alt={`Featured Property ${item}`} />
                  <div className="kso-property-badge">Featured</div>
                </div>
                <div className="kso-property-info">
                  <h3 className="kso-property-price">Property Title</h3>
                  <p className="kso-property-location">Orlando, FL</p>
                  <p className="kso-property-details">3 Beds • 2 Baths • 1,800 sqft</p>
                  <button className="kso-property-button" onClick={handleContactClick}>Learn More</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Kimone */}
      <section id="about" className="kso-about">
        <div className="kso-container">
          <div className="kso-about-content">
            <div className="kso-about-image">
              <img src="/media/kimone/profile.jpg" alt="Kimone Campbell - Orlando Real Estate Agent" />
            </div>
            <div className="kso-about-text">
              <h2>Meet Kimone Campbell</h2>
              <p className="kso-about-subtitle">Your Trusted Local Guide</p>
              <p>With a passion for Central Florida real estate and a commitment to personalized service, I help clients navigate the buying and selling process with confidence. Whether you're a first-time buyer, seasoned investor, or looking to sell your home, I'm here to make your real estate dreams a reality.</p>

              <div className="kso-credentials">
                <div className="kso-credential">
                  <strong>License #:</strong> SL3468311
                </div>
                <div className="kso-credential">
                  <strong>Brokerage:</strong> LPT Realty LLC
                </div>
                <div className="kso-credential">
                  <strong>Areas Served:</strong> Orlando, Kissimmee, Winter Park, Central Florida
                </div>
              </div>

              <div className="kso-about-cta">
                <button className="kso-cta-button" onClick={handleContactClick}>
                  Let's Work Together
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Videos */}
      <section className="kso-videos">
        <div className="kso-container">
          <h2 className="kso-section-title">Real Estate Tips & Market Updates</h2>
          <p className="kso-videos-subtitle">Check out my YouTube channel: Homes by Kimone</p>

          <div className="kso-videos-grid">
            <div className="kso-video-item">
              <iframe
                width="100%"
                height="315"
                src="https://www.youtube.com/embed?listType=user_uploads&list=homesbykimone"
                title="Homes by Kimone - Latest Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="kso-youtube-cta">
            <a
              href="https://www.youtube.com/@homesbykimone"
              target="_blank"
              rel="noopener noreferrer"
              className="kso-youtube-button"
            >
              Visit My YouTube Channel
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="kso-contact">
        <div className="kso-container">
          <h2 className="kso-section-title">Let's Get Started</h2>
          <p className="kso-contact-subtitle">Ready to buy or sell? Contact me today!</p>

          <div className="kso-contact-methods">
            <a href="tel:321-522-2149" className="kso-contact-method">
              <span className="kso-contact-icon">📞</span>
              <span>(321) 522-2149</span>
            </a>
            <a href="mailto:kimone@example.com" className="kso-contact-method">
              <span className="kso-contact-icon">✉️</span>
              <span>Email Me</span>
            </a>
            <button onClick={handleContactClick} className="kso-contact-method kso-contact-method-button">
              <span className="kso-contact-icon">📝</span>
              <span>Request Information</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="kso-footer">
        <div className="kso-container">
          <div className="kso-footer-content">
            <div className="kso-footer-section">
              <h3>Kimone Campbell</h3>
              <p>Real Estate Agent</p>
              <p>LPT Realty LLC</p>
              <p>License #: SL3468311</p>
            </div>

            <div className="kso-footer-section">
              <h3>Contact</h3>
              <p>Phone: (321) 522-2149</p>
              <p>Orlando, FL</p>
            </div>

            <div className="kso-footer-section">
              <h3>Follow Me</h3>
              <div className="kso-social-links">
                <a href="https://www.youtube.com/@homesbykimone" target="_blank" rel="noopener noreferrer">YouTube</a>
                <a href="https://www.facebook.com/kimonesellsorlando" target="_blank" rel="noopener noreferrer">Facebook</a>
                <a href="https://www.instagram.com/kimonesellsorlando" target="_blank" rel="noopener noreferrer">Instagram</a>
                <a href="https://www.linkedin.com/in/kimone-campbell" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              </div>
            </div>
          </div>

          <div className="kso-footer-bottom">
            <p>&copy; 2025 Kimone Campbell. All Rights Reserved.</p>
            <div className="kso-footer-links">
              <a href="/privacy.html">Privacy Policy</a>
              <span>|</span>
              <a href="/tos.html">Terms of Use</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Contact Buttons */}
      <div className="kso-floating-buttons">
        <a href="tel:321-522-2149" className="kso-floating-phone" title="Call Kimone">
          📞
        </a>
        {showScrollTop && (
          <button onClick={scrollToTop} className="kso-floating-scroll" title="Back to Top">
            ↑
          </button>
        )}
      </div>

      {/* Contact Form Modal */}
      {showForm && (
        <ContactForm
          qrCode={qrCode}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Sign In Popup */}
      {showSocialPopup && (
        <div className="kso-social-popup-overlay" onClick={() => setShowSocialPopup(false)}>
          <div className="kso-social-popup" onClick={(e) => e.stopPropagation()}>
            <button className="kso-popup-close" onClick={() => setShowSocialPopup(false)}>×</button>
            <h2 className="kso-popup-title">Sign In</h2>
            <div className="kso-popup-content">
              <p>To sign in and access your saved searches and favorite properties, just fill in your email and phone number below.</p>
              <iframe
                src="//www.easypropertysearch.org/socialmedia/facebook/button/"
                width="292"
                height="45"
                frameBorder="0"
                scrolling="no"
                style={{overflow: 'hidden', marginBottom: '5px'}}
                title="Social Media Buttons"
              >
                <p>Your browser does not support iframes</p>
              </iframe>
              <h3 className="kso-popup-divider">-or-</h3>
              <form className="kso-signin-form">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="kso-form-input"
                />
                <input
                  type="tel"
                  placeholder="Enter your phone number"
                  className="kso-form-input"
                />
                <a href="#" className="kso-forgot-link">Need to reset your phone number?</a>
                <input type="submit" value="Log in" className="kso-submit-button" />
              </form>
            </div>
            <div className="kso-signup-section">
              <h2>Don't Have An Account?</h2>
              <p>We can fix that! <a href="#" className="kso-signup-link">Click here to sign up.</a></p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KimoneSellsOrlando;
